import React, { Children, cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import Clickable from '@move-ui/clickable';
import { ChevronLeftIcon, ChevronIcon } from '@move-ui/icons-asset-catalog';
import styles from './TabBar.module.css';
const DEFAULT_LIST_OFFSET = 100;
const ADDITIONAL_SCROLL_OFFSET = 20;
const TabBar = ({ ariaLabelScrollLeft, ariaLabelScrollRight, className, itemListClassName, whiteBackground, children, role = 'tablist' }) => {
    const listRef = useRef(null);
    const [selectedRef, setSelectedRef] = useState(null);
    const [lastItemRef, setLastItemRef] = useState(null);
    const [showScrollLeftIcon, setShowScrollLeftIcon] = useState(false);
    const [showScrollRightIcon, setShowScrollRightIcon] = useState(false);
    const updateScrollIcons = useCallback(() => {
        if (listRef.current) {
            setShowScrollLeftIcon(listRef.current.scrollLeft > 0);
        }
        if (listRef.current && lastItemRef) {
            // -1 due to decimal scrolling
            setShowScrollRightIcon(listRef.current.scrollLeft +
                listRef.current.offsetWidth -
                (lastItemRef.offsetLeft + lastItemRef.offsetWidth) <
                -1);
        }
    }, [listRef, lastItemRef]);
    const scroll = (amount) => {
        var _a, _b;
        if (listRef.current) {
            (_b = (_a = listRef.current).scrollBy) === null || _b === void 0 ? void 0 : _b.call(_a, {
                behavior: 'smooth',
                top: 0,
                left: amount
            });
            updateScrollIcons();
        }
    };
    const scrollToLeft = () => {
        scroll(-5 * ADDITIONAL_SCROLL_OFFSET);
    };
    const scrollToRight = () => {
        scroll(5 * ADDITIONAL_SCROLL_OFFSET);
    };
    useEffect(() => {
        var _a, _b;
        if (listRef.current && selectedRef) {
            const toRightScrollOffset = selectedRef.offsetLeft -
                listRef.current.scrollLeft -
                DEFAULT_LIST_OFFSET;
            const toLeftScrollOffset = -(listRef.current.scrollLeft +
                listRef.current.offsetWidth -
                (selectedRef.offsetLeft + selectedRef.offsetWidth + DEFAULT_LIST_OFFSET));
            const shouldScrollToRight = toRightScrollOffset < 0;
            const shouldScrollToLeft = toLeftScrollOffset > 0;
            if (shouldScrollToRight || shouldScrollToLeft) {
                (_b = (_a = listRef.current).scrollBy) === null || _b === void 0 ? void 0 : _b.call(_a, {
                    behavior: 'smooth',
                    top: 0,
                    left: shouldScrollToRight
                        ? toRightScrollOffset - ADDITIONAL_SCROLL_OFFSET
                        : toLeftScrollOffset + ADDITIONAL_SCROLL_OFFSET
                });
                updateScrollIcons();
            }
        }
    }, [listRef, selectedRef, updateScrollIcons]);
    useEffect(() => {
        window.addEventListener('resize', updateScrollIcons);
        return () => {
            window.removeEventListener('resize', updateScrollIcons);
        };
    }, [updateScrollIcons]);
    return (React.createElement("div", { className: cx(styles.component, {
            [styles.whiteBackground]: whiteBackground
        }, className), "data-testid": "TabBar" },
        showScrollLeftIcon && (React.createElement(Clickable, { "aria-label": ariaLabelScrollLeft, className: cx(styles.scrollIcon, styles.scrollIconLeft, {
                [styles.whiteBackground]: whiteBackground
            }), onClick: scrollToLeft, "data-testid": "ScrollIconLeft" },
            React.createElement(ChevronLeftIcon, null))),
        React.createElement("ul", { className: cx(styles.itemList, styles.noScrollBar, itemListClassName), ref: listRef, role: role, onScroll: updateScrollIcons }, Children.map(children, (child, index) => cloneElement(child, {
            onUpdateSelected: (element) => {
                setSelectedRef(element);
            },
            setRef: index === children.length - 1
                ? (element) => setLastItemRef(element)
                : undefined
        }))),
        showScrollRightIcon && (React.createElement(Clickable, { "aria-label": ariaLabelScrollRight, className: cx(styles.scrollIcon, styles.scrollIconRight, {
                [styles.whiteBackground]: whiteBackground
            }), onClick: scrollToRight, "data-testid": "ScrollIconRight" },
            React.createElement(ChevronIcon, null)))));
};
export default TabBar;
//# sourceMappingURL=TabBar.js.map